// Single source of truth for the words in PortalTour (portal DOM) and
// PortalTourPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF using the portal";

export const cover = {
  eyebrow: "Portal · 05",
  number: "05",
  title: "Using the portal",
  summary: "The map, the trophies, the share kit, Slack, and keeping your LP seat. A tour of everything in the portal beyond reviewing pitches.",
  accent: "butter",
};

export const blocks = [
  {
    type: "lede",
    text: "The portal is your neighborhood. Review pitches is where the work happens. This guide covers everything else: the map, the trophies, the share kit, Slack, and keeping your LP seat for next year.",
  },
  { type: "heading", level: 1, text: "The layout" },
  {
    type: "p",
    runs: [
      "Tabs live on the left sidebar. Your chapter and name sit at the top; Slack and renewal buttons sit at the bottom. The main panel changes based on which tab you're on. Works the same on desktop and mobile, though we recommend desktop. This is a Windows-95-in-pastels operation.",
    ],
    pdfRuns: [
      "Tabs live on the left sidebar. Your chapter and name sit at the top; Slack and renewal buttons sit at the bottom. The main panel changes based on which tab you're on. Works on desktop and mobile, though desktop is the intended experience.",
    ],
  },
  { type: "heading", level: 1, text: "Pitch map" },
  {
    type: "p",
    runs: [
      "The ",
      { b: "Pitch map" },
      " tab plots every pitch from your chapter on a real map. It's a fun way to see the geographic story of your neighborhood: who's pitching from which block, where past winners landed, how wide the chapter's reach goes.",
    ],
    pdfRuns: [
      "The Pitch map tab plots every pitch from your chapter on a real map. A fun way to see the geographic story of your neighborhood: who's pitching from which block, where past winners landed, how wide the chapter's reach goes.",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [{ b: "Pins" }, " are regular pitches."],
        pdfRuns: [{ b: "Pin icons." }, " Regular pitches."],
      },
      {
        runs: [{ b: "Trophy pins" }, " are past grant winners."],
        pdfRuns: [{ b: "Trophy icons." }, " Past grant winners."],
      },
      {
        runs: [
          { b: "Filters" },
          " on top let you search, narrow by quarter, or flip to ",
          { i: "Show Winners Only" },
          ".",
        ],
        pdfRuns: [{ b: "Filters." }, " Search, narrow by quarter, or flip to Show Winners Only."],
      },
      { runs: ["Click a pin to open that pitch's details."] },
    ],
  },
  {
    type: "fine",
    runs: [
      "Pitches only show up on the map if they have location data from the application. Most do; the occasional one slips through without.",
    ],
    pullUp: true,
  },
  { type: "heading", level: 1, text: "Trophy case", pdfBreak: true },
  {
    type: "p",
    runs: [
      "The ",
      { b: "Trophy case" },
      " tab is where your badges live. Every badge you've unlocked sits front and center with the date you earned it; locked ones show below with a progress bar toward the next unlock. It's the closest thing to a scoreboard we've got.",
    ],
    pdfRuns: [
      "The Trophy case tab is where your badges live. Every badge you've unlocked sits front and center with the date you earned it; locked ones show below with a progress bar toward the next unlock. Closest thing to a scoreboard we've got.",
    ],
  },
  { type: "heading", level: 2, text: "How badges work" },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Earn them by participating." },
          " Reviewing pitches, leaving team notes, voting with the group, being consistent over time.",
        ],
      },
      {
        runs: [
          { b: "Progress tracks automatically." },
          " No claim buttons. The system just watches your activity.",
        ],
      },
      {
        runs: [
          { b: "Some are quiet." },
          " A few badges are easter eggs. We're not going to spoil them.",
        ],
      },
    ],
  },
  {
    type: "callout",
    label: "When you earn one",
    runs: [
      "A notification pops up right after the action that triggered it, usually a review submission. The new badge also appears in your Trophy case immediately.",
    ],
  },
  { type: "heading", level: 1, text: "Chapter members" },
  {
    type: "p",
    runs: [
      "The ",
      { b: "Chapter members" },
      " tab is a directory of everyone in your neighborhood, the people whose opinions your ratings sit alongside. Each member card shows:",
    ],
    pdfRuns: [
      "The Chapter members tab is a directory of everyone in your neighborhood, the people whose opinions your ratings sit alongside. Each card shows:",
    ],
  },
  {
    type: "list",
    items: [
      { runs: [{ b: "Name and role." }, " Who they are and what they do day-to-day."] },
      {
        runs: [
          { b: "Member since." },
          " Month and year they joined, so you can tell a founding LP from last quarter's addition.",
        ],
      },
      { runs: [{ b: "LinkedIn." }, " One click to their profile."] },
      { runs: [{ b: "Recent badges." }, " Their last five unlocked trophies."] },
    ],
  },
  {
    type: "p",
    runs: [
      "It's also a friendly way to see how your Trophy case stacks up against the rest of the chapter, and a reminder that the people you'll meet at the next dinner are already on your team.",
    ],
  },
  { type: "heading", level: 1, text: "Share kit (social cards)" },
  {
    type: "p",
    runs: [
      "The ",
      { b: "Social" },
      " tab has four pre-made cards you can download and post. Each one is sized for Instagram / LinkedIn / X and pulls your name and chapter automatically:",
    ],
    pdfRuns: [
      "The Social tab has four pre-made cards you can download and post. Each is sized for Instagram / LinkedIn / X and pulls your name and chapter automatically:",
    ],
  },
  {
    type: "list",
    items: [
      { runs: [{ b: "Welcome to GNF." }, " Introduce yourself to your new chapter."] },
      { runs: [{ b: "Badge Achievements." }, " Show off the trophies you've earned."] },
      { runs: [{ b: "Chapter Impact." }, " Your chapter's funded-business tally."] },
      { runs: [{ b: "LP Recruitment." }, " Rally new LPs into the network."] },
    ],
  },
  {
    type: "p",
    runs: [
      "Click ",
      { b: "Download" },
      " on any card and a PNG saves to your device. The cards are the easiest way to help your chapter grow. One post after you join is often enough to surface two or three new prospective LPs.",
    ],
    pdfRuns: [
      "Click Download on any card and a PNG saves to your device. The cards are the easiest way to help your chapter grow. One post after you join often surfaces two or three new prospective LPs.",
    ],
  },
  { type: "heading", level: 1, text: "Join Slack", pdfBreak: true },
  {
    type: "p",
    runs: [
      "At the bottom of the sidebar you'll find a ",
      { b: "Join Slack" },
      " button. One click opens our shared workspace invite in a new tab. Every chapter has its own channel; there's a national channel for cross-chapter chatter, meme-sharing, and asking Chapter Directors for help. This is where the neighborhood actually hangs out between meetings.",
    ],
    pdfRuns: [
      "At the bottom of the sidebar you'll find a Join Slack button. One click opens our shared workspace invite in a new tab. Every chapter has its own channel; there's a national channel for cross-chapter chatter and asking Chapter Directors for help. This is where the neighborhood actually hangs out between meetings.",
    ],
  },
  { type: "heading", level: 1, text: "LP membership renewal" },
  {
    type: "p",
    runs: [
      "Directly above the Slack button, under ",
      { b: "LP Membership" },
      ", you'll see one or two renewal buttons depending on how your chapter sets things up:",
    ],
    pdfRuns: [
      "Directly above the Slack button, under LP Membership, you'll see one or two renewal buttons depending on how your chapter sets things up:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Renew Annual." },
          " One payment, one year of membership. The simplest option; most LPs pick this.",
        ],
        pdfRuns: [{ b: "Renew Annual." }, " One payment, one year of membership. Most LPs pick this."],
      },
      {
        runs: [
          { b: "Renew Semi-Annual." },
          " A smaller payment every six months. Useful if your chapter prefers the lighter cadence or you're spreading cost.",
        ],
        pdfRuns: [
          { b: "Renew Semi-Annual." },
          " A smaller payment every six months. Useful if your chapter prefers the lighter cadence.",
        ],
      },
    ],
  },
  {
    type: "p",
    runs: [
      "Clicking either button opens Stripe in a new tab. Both plans",
      { b: " auto-renew" },
      " on the same cadence going forward, so once you're in, you stay in. No calendar reminder required. Stripe emails a receipt each time and gives you a link to update your card or cancel.",
    ],
    pdfRuns: [
      "Clicking either button opens Stripe in a new tab. Both plans auto-renew on the same cadence going forward, so once you're in, you stay in. No calendar reminder required. Stripe emails a receipt each time and gives you a link to update your card or cancel.",
    ],
  },
  {
    type: "callout",
    label: "Prefer not to use Stripe?",
    runs: [
      "Check, ACH, and wire are all welcome. They just don't live in the portal. Message your Chapter Director directly and they'll coordinate the payment and keep your membership active.",
    ],
  },
  { type: "heading", level: 1, text: "Sign-out & account" },
  {
    type: "p",
    runs: [
      "Your name at the top of the sidebar is your account area. Click it to sign out. Password resets and email changes are handled by your Chapter Director in the admin panel; just ping them in Slack.",
    ],
  },
  {
    type: "callout",
    label: "Something feel broken?",
    runs: [
      "Post in ",
      { b: "#help" },
      " on the national Slack or email",
      { a: { href: "mailto:hello@goodneighbor.fund", text: " hello@goodneighbor.fund" } },
      ". Screenshots help us move faster.",
    ],
    pdfRuns: [
      "Post in #help on the national Slack or email hello@goodneighbor.fund. Screenshots help us move faster.",
    ],
  },
];
